import { useMemo } from 'react';

import { useNavigate } from 'react-router-dom';

import { ENTITY_TYPES, SERVICE_ENTITY_TYPES } from '@/shared/api/openmetadata';
import { mapTagsToBasicInfo } from '@/shared/lib/tag';
import { getTagRoute } from '@/shared/lib/tags';
import { DrawerDescription, DrawerDomainList, DrawerEmptyValue, DrawerOwnerList } from '@/shared/ui/AssetMetadata';
import { BadgeList } from '@/shared/ui/BadgeList';
import type { DrawerSection, InfoRow } from '@/shared/ui/lineage/LineageNodeDrawer';
import { TierDisplay } from '@/shared/ui/TierDisplay';

import { filterEntityLabels } from '@/entities/search';

import type { PlatformLineageEntity } from '@/features/lineage/api';

const createOwnerSection = (
  id: string,
  title: string,
  owners: PlatformLineageEntity['owners'] | PlatformLineageEntity['experts']
): DrawerSection => ({
  id,
  title,
  content:
    owners && owners.length > 0 ? (
      <DrawerOwnerList owners={owners} />
    ) : (
      <DrawerEmptyValue>{title} 없음</DrawerEmptyValue>
    ),
});

const createDescriptionSection = (description?: string): DrawerSection => ({
  id: 'description',
  title: '설명',
  content: description ? (
    <DrawerDescription description={description} />
  ) : (
    <DrawerEmptyValue>데이터를 찾을 수 없습니다</DrawerEmptyValue>
  ),
});

/**
 * 계보 페이지의 LineageNodeDrawer 데이터를 생성하는 훅
 * API 호출 없이 PlatformLineageResponse의 entity 데이터를 사용
 */
export const usePlatformLineageNodeDrawer = (entity: PlatformLineageEntity | null) => {
  const navigate = useNavigate();

  const infoRows = useMemo((): InfoRow[] => {
    if (!entity) return [];

    const { entityType, serviceType, tier } = entity;

    if (SERVICE_ENTITY_TYPES.includes(entityType as (typeof SERVICE_ENTITY_TYPES)[number])) {
      return [
        {
          label: '계층',
          value: tier ? (
            <TierDisplay tierName={tier.displayName || tier.name} />
          ) : (
            <DrawerEmptyValue>-</DrawerEmptyValue>
          ),
        },
        {
          label: '서비스 유형',
          value: serviceType || <DrawerEmptyValue>-</DrawerEmptyValue>,
        },
      ];
    }

    return [];
  }, [entity]);

  const sections = useMemo((): DrawerSection[] => {
    if (!entity) return [];

    const { entityType, tags, tier, description, owners, experts, domains } = entity;
    const result: DrawerSection[] = [];

    const classificationTags = mapTagsToBasicInfo(
      filterEntityLabels(tags, {
        includeOnlySource: 'Classification',
        requireConfirmed: true,
      })
    );

    const allTags = [...classificationTags, ...(tier ? mapTagsToBasicInfo([tier]) : [])];

    const uniqueTags = allTags.filter((tag, index, self) => index === self.findIndex((t) => t.name === tag.name));
    const hasTags = uniqueTags.length > 0;

    const tagBadgeItems = uniqueTags;

    const tagsSection: DrawerSection = {
      id: 'tags',
      title: '태그',
      content: hasTags ? (
        <BadgeList
          items={tagBadgeItems}
          onItemClick={(tagFQN) => navigate(getTagRoute(tagFQN))}
          iconName="tag"
          isEllipsis={false}
        />
      ) : (
        <DrawerEmptyValue>연결된 태그가 없습니다</DrawerEmptyValue>
      ),
    };

    if (entityType === ENTITY_TYPES.DOMAIN) {
      result.push(
        tagsSection,
        createDescriptionSection(description),
        createOwnerSection('owners', '소유자', owners),
        createOwnerSection('experts', '전문가', experts)
      );
      return result;
    }

    result.push(createOwnerSection('owners', '소유자', owners));

    result.push({
      id: 'domains',
      title: '도메인',
      content:
        domains && domains.length > 0 ? (
          <DrawerDomainList
            domains={domains.map((d) => ({
              id: d.id,
              name: d.name,
              displayName: d.displayName,
              fullyQualifiedName: d.fullyQualifiedName,
            }))}
          />
        ) : (
          <DrawerEmptyValue>연결된 도메인이 없습니다</DrawerEmptyValue>
        ),
    });

    result.push(tagsSection, createDescriptionSection(description));

    if (entityType === ENTITY_TYPES.DATA_PRODUCT) {
      result.push(createOwnerSection('experts', '전문가', experts));
    }

    return result;
  }, [entity, navigate]);

  return {
    infoRows,
    sections,
  };
};
